import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import {
    Button,
    Input,
    DatePicker,
    Select,
    Card,
    Spin,
    Pagination,
    message,
    Badge,
    Modal,
    Form,
    Input as AntInput,
    Tooltip,
    Tag,
    Space,
    Popconfirm,
    Checkbox,
    Tabs,
    Drawer,
    Image
} from 'antd';
import { SearchOutlined, ReloadOutlined, CalendarOutlined, FilterOutlined, MenuOutlined, CheckOutlined, LockOutlined, ClockCircleOutlined, CloseOutlined, CloseCircleOutlined, EditOutlined, PlusOutlined, CheckSquareOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageTitle from '../../Ui/PageTitle';
import { TbCancel } from 'react-icons/tb';
import { FaCheck, FaUser, FaClock, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';
import { IoPersonAddOutline, IoReload } from "react-icons/io5";
import { MdOutlineArticle } from 'react-icons/md';
import ReportModal from './ReportModal';
import { useClinics } from '../../../hooks/useClinics';
import { useBookings } from '../../../hooks/useBookings';
import { useQueryClient } from '@tanstack/react-query';
import { FaLock } from 'react-icons/fa6';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import { Link, useNavigate } from 'react-router-dom';
import { CgCheck } from 'react-icons/cg';
import { BiBlock } from 'react-icons/bi';
import PatientAssignmentModal from '../../Modals/SelectPatientModal';
import AddReportModal from '../../Report/AddReportModal';
import InvoiceModal from '../../Modals/InvoiceModal';
import { CiSquareCheck } from 'react-icons/ci';
import { LuSquareCheck } from 'react-icons/lu';
import { FiCheckSquare } from 'react-icons/fi';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = AntInput;
const { TabPane } = Tabs;
const statusEnum = {
    Pending: 0,
    Cancelled: 1,
    Completed: 2,   
    Skipped:3,
    Active:4
};
const statusConfig = {
    Pending: {
        color: 'bg-gradient-to-r from-amber-400 to-orange-500',
        textColor: 'text-yellow-400',
        icon: <ClockCircleOutlined />,
        bgLight: 'bg-amber-50',
        borderColor: 'border-amber-200'
    },
    Cancelled: {
        color: 'bg-gradient-to-r from-red-400 to-red-600',
        textColor: 'text-red-400',
        icon: <BiBlock className='inline mb-[1px]' />,
        bgLight: 'bg-red-50',
        borderColor: 'border-red-200'
    },
    Completed: {
        color: 'bg-gradient-to-r from-green-400 to-emerald-500',
        icon: <CheckOutlined />,
        textColor: 'text-green-400',
        bgLight: 'bg-green-50',
        borderColor: 'border-green-200'
    },
    Skiped: {
        color: 'bg-gradient-to-r from-gray-400 to-gray-500',
        textColor: 'text-gray-500',
        icon: <ReloadOutlined />,
        bgLight: 'bg-gray-50',
        borderColor: 'border-gray-200'
    },
    Active: {
        color: 'bg-gradient-to-r from-green-400 to-green-500',
        textColor: 'text-green-500',
        icon: <ClockCircleOutlined />,
        bgLight: 'bg-green-50',
        borderColor: 'border-green-200'
    }
};

const useResponsive = () => {
    const [breakpoint, setBreakpoint] = useState('lg');
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
        const updateBreakpoint = () => {
            const width = window.innerWidth;
            if (width < 640) {
                setBreakpoint('sm');
                setIsMobile(true);
                setIsTablet(false);
            } else if (width < 768) {
                setBreakpoint('md');
                setIsMobile(false);
                setIsTablet(true);
            } else if (width < 1024) {
                setBreakpoint('lg');
                setIsMobile(false);
                setIsTablet(true);
            } else if (width < 1280) {
                setBreakpoint('xl');
                setIsMobile(false);
                setIsTablet(false);
            } else {
                setBreakpoint('2xl');
                setIsMobile(false);
                setIsTablet(false);
            }
        };

        updateBreakpoint();
        window.addEventListener('resize', updateBreakpoint);
        return () => window.removeEventListener('resize', updateBreakpoint);
    }, []);

    return { breakpoint, isMobile, isTablet };
};



const BookingCard = memo(({loadingStatus, booking, index, onComplete, onCancel, onGuestAssignment, onSkip, onOpenReportModal, updateStatusMutation, isMobile, onEditInvoice,    
  onCreateInvoice ,completing   ,onMakeActive }) => {
const handleEditInvoiceClick = useCallback((e) => {
    e.stopPropagation();
    onEditInvoice(booking);
  }, [booking, onEditInvoice]);

  const handleCreateInvoiceClick = useCallback((e) => {
    e.stopPropagation();
    onCreateInvoice(booking);
  }, [booking, onCreateInvoice]);

    const status = statusConfig[booking.status] || statusConfig.Pending;
    const navigate = useNavigate();

    const isToday = dayjs(booking.bookingDate).isAfter(dayjs(), 'day');
    const isActionsDisabled = isToday;

    console.log("Rendering BookingCard:", booking);

    const handleCompleteClick = useCallback(() => {
        if (!isActionsDisabled) {
            onComplete(booking);
        }
    }, [booking, onComplete, isActionsDisabled]);

    const handleCancelClick = useCallback(() => {
        onCancel(booking);
    }, [booking, onCancel]);

    const handleSkipClick = useCallback(() => {
        if (!isActionsDisabled) {
            updateStatusMutation.mutate({
                bookingId: booking.id,
                newStatus: 'Skipped',
            });
        }
    }, [booking.id, updateStatusMutation, isActionsDisabled]);
    const handleActiveClick = useCallback(() => {
            updateStatusMutation.mutate({
                bookingId: booking.id,
                newStatus: 'Active',
            });
        
    }, [booking.id, updateStatusMutation, isActionsDisabled]);

    const handleGuestAssignmentClick = useCallback(() => {
        if (!isActionsDisabled) {
            onGuestAssignment(booking);
        }
    }, [booking, onGuestAssignment, isActionsDisabled]);

    const handlePatientClick = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const cacheKey = `patient_${booking.patientId}`;
        const cachedPatient = sessionStorage.getItem(cacheKey);

        if (cachedPatient) {
            const patientData = JSON.parse(cachedPatient);
            navigate(`/patients/${booking.patientId}`, {
                state: { patient: patientData }
            });
            return;
        }

        try {
            console.log('Loading patient data for:', booking);

            const response = await drEasyApi.get('/api/Doctor/GetDoctorPatients', {
                params: {
                    Page: 1,
                    PageSize: 100,
                    ClinicId: booking.clinicId || undefined,
                }
            });

            const patients = response.data.patients || [];
            console.log('Fetched patients:', patients);
            const completePatientData = patients.find(patient => patient.id === booking.profileId);

            if (completePatientData) {
                const patientForNavigation = {
                    ...completePatientData,
                    fullName: completePatientData.fullName || booking.patientName,
                    imageId: completePatientData.imageId || booking.patientProfile,
                    clinics: completePatientData.clinics || (booking.clinicName ? [{ name: booking.clinicName }] : []),
                    reportId: completePatientData.reportId || null,
                };

                sessionStorage.setItem(cacheKey, JSON.stringify(patientForNavigation));
                setTimeout(() => sessionStorage.removeItem(cacheKey), 5 * 60 * 1000);

                navigate(`/patients/${booking.patientId}`, {
                    state: { patient: patientForNavigation }
                });
            } else {
                throw new Error('Patient not found');
            }
        } catch (error) {
            console.error('Error fetching patient data:', error);
            message.error('Failed to load patient details');

            navigate(`/patients/${booking.patientId}`, {
                state: {
                    patient: {
                        id: booking.patientId,
                        fullName: booking.patientName,
                        email: '',
                        imageId: booking.patientProfile || null,
                        clinics: booking.clinicName ? [{ name: booking.clinicName }] : [],
                        isDeactivated: false,
                        skipedCount: 0,
                        reportId: null,
                        hasInvoice:booking.hasInvoice || false,
                    }
                }
            });
        }
    }, [navigate, booking]);

      const handleActiveButtonClick = useCallback(() => {
      onMakeActive(booking);
    //   setCurrentPatientId(booking.patientId)
    }, [booking, onMakeActive]);


    return (
        <div
            className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${status.borderColor} overflow-hidden ${isMobile ? 'mb-4' : ''
                } ${isActionsDisabled ? 'opacity-75' : ''}`}
            style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
            }}
        >
            <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-bold ${status.bgLight} border ${status.borderColor} ${status.textColor} shadow-lg transform ${isMobile ? 'rotate-0' : 'rotate-3 group-hover:rotate-0'
                } transition-transform duration-300`}>
                {status.icon} {booking.status}
            </div>

            {booking.isGuest &&
                <div className={`absolute top-3 right-32 sm:top-4 sm:right-32 px-2 py-[.5px] sm:px-3 rounded-full text-xs font-bold border bg-red-100 border-red-300 text-red-400 shadow-lg transform ${isMobile ? 'rotate-0' : 'rotate-3 group-hover:rotate-0'
                    } transition-transform duration-300`}>
                    Guest
                </div>
            }

            {/* Card Content */}
            <div className="p-4 sm:p-6">
                <div className={`flex items-center mb-3 sm:mb-4 ${isMobile ? 'flex-wrap' : ''}`}>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
                        {booking.patientProfile ? (
                            <img
                                src={booking.patientProfile ? getImg(booking.patientProfile) : 'https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg'}
                                className='rounded-full border-primary w-full h-full object-cover'
                            />
                        ) : (
                            <span>{booking.patientName.charAt(0).toUpperCase()}</span>
                        )}
                    </div>
                    <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                        {/* FIXED: Proper clickable patient name */}
                        <button
                            disabled={booking.isGuest}
                            onClick={handlePatientClick}
                            className={`font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 ${isMobile ? 'text-base truncate' : 'text-lg'
                                } hover:underline cursor-pointer bg-transparent border-none p-0 text-left w-full`}
                        >
                            {booking.patientName}
                        </button>
                        <div className="flex items-center text-xs sm:text-sm text-gray-500">
                            <FaMapMarkerAlt className="mr-1 flex-shrink-0" />
                            <span className={isMobile ? 'truncate' : ''}>{booking.clinicName}</span>
                        </div>
                    </div>
                </div>

                {/* Date & Time */}
                <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 ${status.bgLight} border ${status.borderColor}`}>
                    <div className={`${isMobile ? 'space-y-2' : 'flex items-center justify-between'}`}>
                        <div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-700">
                                📅 {dayjs(booking.bookingDate).format(isMobile ? 'MMM D' : 'MMM D, YYYY')}

                            </div>
                            <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1">
                                <FaClock className="mr-2 flex-shrink-0" />
                                <span className={isMobile ? 'text-xs' : ''}>
                                    {dayjs(booking.startTime, 'HH:mm').format('h:mm A')} - {dayjs(booking.endTime, 'HH:mm').format('h:mm A')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className={`flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100 ${isMobile ? 'flex-wrap gap-2' : ''
                    }`}>
                    <div className={`flex ${isMobile ? 'gap-1' : 'space-x-2'} ${isMobile ? 'flex-wrap' : ''}`}>
                        {/* {booking.isGuest && (
                            <Tooltip title="Assign to user">
                                <Button
                                    type=""
                                    icon={<IoPersonAddOutline className='text-lg' />}
                                    onClick={() => onGuestAssignment(booking)}
                                    size={isMobile ? 'small' : 'middle'}
                                    className="bg-red-400 to-red-600 border-none text-white rounded-full hover:bg-red-500 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                                />
                            </Tooltip>
                        )} */}

                      {booking.status === 'Pending' && (
              <>
                 <Tooltip title={ "Mark as Active"}>
                  <Button
                    type=""
                    icon={<LuSquareCheck className='text-xl' />}
                    onClick={handleActiveClick}
                    // disabled={isActionsDisabled}
                    size={isMobile ? 'small' : 'middle'}
                    className={`bg-green-600 to-green-500 border-none text-white hover:bg-green-400' rounded-full flex items-center justify-center shadow-lg transition-all duration-300 `}
                  />
                </Tooltip>
                {/* <Tooltip title={isActionsDisabled ? "Actions only available for today's bookings" : "Mark as Completed"}> */}
                <Tooltip title={"Mark as Completed"}>
                  <Button
                    type=""
                    loading={completing}
                    icon={<FaCheck />}
                    onClick={() => onOpenReportModal(booking)}
                    disabled={completing}
                    size={isMobile ? 'small' : 'middle'}
                    className={`${completing ? 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-500 border-none text-white hover:bg-green-600'} rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${!isActionsDisabled ? 'hover:shadow-xl transform hover:scale-110' : ''}`}
                  />
                </Tooltip>
             

                <Tooltip title={isActionsDisabled ? "Actions only available for today's bookings" : "Mark as Skipped"}>
                  <Button
                    type=""
                    icon={<IoReload />}
                    onClick={handleSkipClick}
                    disabled={isActionsDisabled}
                    size={isMobile ? 'small' : 'middle'}
                    className={`${isActionsDisabled ? 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gray-400 to-gray-500 border-none text-white hover:bg-yellow-400'} rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${!isActionsDisabled ? 'hover:shadow-xl transform hover:scale-110' : ''}`}
                  />
                </Tooltip>

             

                <Popconfirm
                  title="Are you sure you want to cancel this booking?"
                  onConfirm={handleCancelClick}
                  okText="Yes"
                  cancelText="No"
                  placement={isMobile ? 'top' : 'bottom'}
                >
                  <Tooltip title={"Cancel Booking"}>
                    <Button
                      type=""
                      icon={<TbCancel />}
                      size={isMobile ? 'small' : 'middle'}
                      className="bg-red-400 to-red-600 border-none text-white hover:bg-red-500 rounded-full flex items-center justify-center shadow-lg transition-all duration-300"
                    />
                  </Tooltip>
                </Popconfirm>
              </>
            )}

            {booking.status === 'Active' && (

  <Tooltip title={"Mark as Completed"}>
                  <Button
                    type=""
                    loading={completing}
                    icon={<FaCheck />}
                    onClick={() => onOpenReportModal(booking)}
                    disabled={completing}
                    size={isMobile ? 'small' : 'middle'}
                    className={`${completing ? 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed' : 'bg-emerald-500 border-none text-white hover:bg-green-600'} rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${!isActionsDisabled ? 'hover:shadow-xl transform hover:scale-110' : ''}`}
                  />
                </Tooltip>

            )}

            {/* {booking.status === 'Completed' && (
              <>
                {booking.hasInvoice ? (
                  <Tooltip title="Edit Invoice">
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditInvoiceClick}
                      size={isMobile ? 'small' : 'middle'}
                      className="bg-[#298E9F] border-none rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                      style={{ backgroundColor: '#298E9F' }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Create Invoice">
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleCreateInvoiceClick}
                      size={isMobile ? 'small' : 'middle'}
                      className="bg-green-500 border-none rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                    />
                  </Tooltip>
                )}
              </>
            )} */}

                    </div>

                </div>
            </div>

            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
        </div>
    );
});
const ServiceModal = memo(({
    visible,
    onCancel,
    onOk,
    loading,
    services,
    servicesLoading,
    selectedServices,
    onServiceChange,
    form,
    isMobile
}) => (
    <Modal
        title={
            <div className="flex items-center">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mr-2 sm:mr-3">
                    <FaCheck className="text-white text-xs sm:text-sm" />
                </div>
                <span className={isMobile ? 'text-sm' : ''}>Complete Booking - Select Services</span>
            </div>
        }
        open={visible}
        onOk={onOk}
        onCancel={onCancel}
        okText=" Complete Booking"
        cancelText="Cancel"
        confirmLoading={loading}
        width={isMobile ? '95vw' : 600}
        className="modern-modal"
        centered={isMobile}
    >
        <div className="mb-4">
            <p className={`text-gray-600 mb-3 ${isMobile ? 'text-sm' : ''}`}>
                Please select the services that were provided to the patient:
            </p>
            <Form form={form} layout="vertical">
                <Spin spinning={servicesLoading}>
                    <div className={`${isMobile ? 'max-h-60' : 'max-h-80'} overflow-y-auto border rounded-xl p-3 bg-gray-50`}>
                        {services.length > 0 ? (
                            <div className="space-y-4">
                                {services.map((service) => (
                                    <div key={service.id} className={`bg-white rounded-lg ${isMobile ? 'p-3' : 'p-4'} shadow-sm hover:shadow-md transition-shadow duration-300`}>
                                        <Checkbox
                                            checked={selectedServices.includes(service.id)}
                                            onChange={(e) => onServiceChange(service.id, e.target.checked)}
                                            className="w-full"
                                        >
                                            <div className={`flex items-center ${isMobile ? 'gap-2' : 'gap-3'}`}>
                                                <img
                                                    src={getImg(service.photoID)}
                                                    className={`${isMobile ? 'w-8 h-8' : 'w-12 h-12'} rounded-lg object-cover shadow-sm`}
                                                    alt={service.title}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className={`font-medium text-gray-800 ${isMobile ? 'text-sm truncate' : ''}`}>
                                                        {service.title}
                                                    </div>
                                                    {service.price >= 0 && (
                                                        <div className={`font-bold text-green-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                                            💰 ${service.price}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Checkbox>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center text-gray-500 py-8">
                                <div className={`${isMobile ? 'text-2xl' : 'text-4xl'} mb-2`}>🔍</div>
                                <div className={isMobile ? 'text-sm' : ''}>No services available</div>
                            </div>
                        )}
                    </div>
                </Spin>

                {selectedServices.length > 0 && (
                    <div className={`mt-4 ${isMobile ? 'p-3' : 'p-4'} bg-blue-50 rounded-xl border border-blue-200`}>
                        <div className={`font-medium text-blue-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            ✅ Selected Services: {selectedServices.length}
                        </div>
                    </div>
                )}
            </Form>
        </div>
    </Modal>
));

const BookingsScreen = () => {
    const { breakpoint, isMobile, isTablet } = useResponsive();
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [currentReportBooking, setCurrentReportBooking] = useState(null);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [servicesModalVisible, setServicesModalVisible] = useState(false);
    const [currentBooking, setCurrentBooking] = useState(null);
    const [currentCompleteBooking, setCurrentCompleteBooking] = useState(null);
    const [doctorServices, setDoctorServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [activeTab, setActiveTab] = useState('today');
    const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false);
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [servicesForm] = Form.useForm();
    const [patientAssignmentModalVisible, setPatientAssignmentModalVisible] = useState(false);
    const [currentGuestBooking, setCurrentGuestBooking] = useState(null);
 const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [currentInvoiceBooking, setCurrentInvoiceBooking] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [completing,setCompleting]=useState(false)
  const [currentStatus,setCurrentStatus]=useState(null)
   const [patientInfoModalVisible, setPatientInfoModalVisible] = useState(false);
  const [currentActivateBooking, setCurrentActivateBooking] = useState(null);
  const handleEditInvoice = useCallback(async (booking) => {
    try {
      setLoadingInvoice(true);
      setCurrentInvoiceBooking(booking);
      
      const response = await drEasyApi.get('/api/BookingInvoice/Get', {
        params: { BookingId: booking.id }
      });
      
      setInvoiceData(response.data);
      setInvoiceModalVisible(true);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      message.error('Failed to load invoice');
    } finally {
      setLoadingInvoice(false);
    }
  }, []);

  // Handler to create new invoice
  const handleCreateInvoice = useCallback((booking) => {
    // Create patient object for AddReportModal
    const patientData = {
      id: booking.patientId,
      accountId: booking.patientId,
      fullName: booking.patientName,
      imageId: booking.patientProfile,
      clinicName: booking.clinicName,
      clinicId: booking.clinicId,
      reportId: booking.reportId || null,
      profileId: booking.profileId
    };

    setSelectedPatientForReport(patientData);
    setSelectedBooking(booking);
    setAddReportModalVisible(true);
  }, []);
    const handleGuestAssignmentClick = useCallback((booking) => {
        setCurrentGuestBooking(booking);
        setPatientAssignmentModalVisible(true);
    }, []);
    const [addReportModalVisible, setAddReportModalVisible] = useState(false);
const [selectedBooking ,setSelectedBooking]=useState(null)
    const [selectedPatientForReport, setSelectedPatientForReport] = useState(null); // Add this
    // In BookingsScreen component, add this handler

    const handlePatientAssignmentModalClose = useCallback(() => {
        setPatientAssignmentModalVisible(false);
        setCurrentGuestBooking(null);
    }, []);
    const getPageSize = useMemo(() => {
        if (isMobile) return 4;
        if (isTablet) return 6;
        return 12;
    }, [isMobile, isTablet]);

    const getGridCols = useMemo(() => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        if (breakpoint === 'xl') return 3;
        return 4;
    }, [isMobile, isTablet, breakpoint]);

    const [todayPagination, setTodayPagination] = useState({
        current: 1,
        pageSize: getPageSize,
        total: 0,
    });

    const [tomorrowPagination, setTomorrowPagination] = useState({
        current: 1,
        pageSize: getPageSize,
        total: 0,
    });

    const [yesterdayPagination, setYesterdayPagination] = useState({
        current: 1,
        pageSize: getPageSize,
        total: 0,
    });

    const [allPagination, setAllPagination] = useState({
        current: 1,
        pageSize: getPageSize,
        total: 0,
    });

    // Update page sizes when breakpoint changes
    useEffect(() => {
        const newPageSize = getPageSize;
        setTodayPagination(prev => ({ ...prev, pageSize: newPageSize }));
        setTomorrowPagination(prev => ({ ...prev, pageSize: newPageSize }));
        setYesterdayPagination(prev => ({ ...prev, pageSize: newPageSize }));
        setAllPagination(prev => ({ ...prev, pageSize: newPageSize }));
    }, [getPageSize]);

    // Separate filters for each tab
    const [todayFilters, setTodayFilters] = useState({
        patientName: '',
        clinicId: undefined,
        fromDate: dayjs().startOf('day'),
        toDate: dayjs().endOf('day'),
    });

    const [tomorrowFilters, setTomorrowFilters] = useState({
        patientName: '',
        clinicId: undefined,
        fromDate: dayjs().add(1, 'day').startOf('day'),
        toDate: dayjs().add(1, 'day').endOf('day'),
    });

    const [yesterdayFilters, setYesterdayFilters] = useState({
        patientName: '',
        clinicId: undefined,
        fromDate:undefined,
        toDate: dayjs().subtract(1, 'day').endOf('day'),
    });

    const [allFilters, setAllFilters] = useState({
        status: undefined,
        patientName: '',
        clinicId: undefined,
        fromDate: undefined,
        toDate: undefined,
    });

    const [isPrivate, setIsPRivate] = useState(false);

    // Use custom hooks for each tab
    const { clinicsQuery } = useClinics({
        pageNumber: 1,
        pageSize: 100,
        staleTime: Infinity
    });

    const { bookingsQuery: todayBookingsQuery, updateStatusMutation } = useBookings(todayPagination, todayFilters);
    const { bookingsQuery: tomorrowBookingsQuery } = useBookings(tomorrowPagination, tomorrowFilters);
    const { bookingsQuery: yesterdayBookingsQuery } = useBookings(yesterdayPagination, yesterdayFilters);
    const { bookingsQuery: allBookingsQuery } = useBookings(allPagination, allFilters);

    // Memoized functions to prevent recreations
    const getCurrentTabData = useMemo(() => {
        switch (activeTab) {
            case 'today':
                return { query: todayBookingsQuery, pagination: todayPagination, setPagination: setTodayPagination };
            case 'tomorrow':
                return { query: tomorrowBookingsQuery, pagination: tomorrowPagination, setPagination: setTomorrowPagination };
            case 'yesterday':
                return { query: yesterdayBookingsQuery, pagination: yesterdayPagination, setPagination: setYesterdayPagination };
            case 'all':
                return { query: allBookingsQuery, pagination: allPagination, setPagination: setAllPagination };
            default:
                return { query: todayBookingsQuery, pagination: todayPagination, setPagination: setTodayPagination };
        }
    }, [activeTab, todayBookingsQuery, tomorrowBookingsQuery, yesterdayBookingsQuery, allBookingsQuery, todayPagination, tomorrowPagination, yesterdayPagination, allPagination]);

    const getCurrentFilters = useMemo(() => {
        switch (activeTab) {
            case 'today':
                return todayFilters;
            case 'tomorrow':
                return tomorrowFilters;
            case 'yesterday':
                return yesterdayFilters;
            case 'all':
                return allFilters;
            default:
                return todayFilters;
        }
    }, [activeTab, todayFilters, tomorrowFilters, yesterdayFilters, allFilters]);

    const setCurrentFilters = useCallback((newFilters) => {
        switch (activeTab) {
            case 'today':
                setTodayFilters(newFilters);
                break;
            case 'tomorrow':
                setTomorrowFilters(newFilters);
                break;
            case 'yesterday':
                setYesterdayFilters(newFilters);
                break;
            case 'all':
                setAllFilters(newFilters);
                break;
            default:
                setTodayFilters(newFilters);
        }
    }, [activeTab]);

    // Fetch doctor services
    const fetchDoctorServices = useCallback(async () => {
        setServicesLoading(true);
        try {
            const response = await drEasyApi.get('/api/Services/GetMyServices');
            setDoctorServices(response.data);
        } catch (error) {
            console.error('Error fetching services:', error);
            message.error('Error fetching services');
        } finally {
            setServicesLoading(false);
        }
    }, []);

    // Memoized event handlers
    const handleCompleteClick = useCallback(async (booking) => {
        setCurrentCompleteBooking(booking);
        // setSelectedServices([]);
        setServicesModalVisible(true);
        await fetchDoctorServices();
    }, [fetchDoctorServices]);

    const handleCancelClick = useCallback((booking) => {
        setCurrentBooking(booking);
        setCancelModalVisible(true);
    }, []);
 
  const handleMakeActive = useCallback((booking) => {
    setCurrentActivateBooking(booking);
    setPatientInfoModalVisible(true);
  }, []);
    const handleCompleteConfirm = useCallback(() => {
                setCompleting(true)

            try{
                updateStatusMutation.mutate({
                    bookingId: currentCompleteBooking.id,
                    newStatus: 'Completed',
                    serviceIds: selectedServices.length > 0 ? selectedServices : []
                });
                setServicesModalVisible(false);
                servicesForm.resetFields();
                setSelectedServices([]);
            }catch(info) {
                console.log('Validate Failed:', info);
            }finally{
                        setCompleting(false)

            }
    }, [updateStatusMutation, currentCompleteBooking, selectedServices, servicesForm]);

    const handleCancelConfirm = useCallback(() => {
        form
            .validateFields()
            .then((values) => {
                updateStatusMutation.mutate({
                    bookingId: currentBooking.id,
                    newStatus: 'Cancelled',
                    reason: values.reason
                });
                handleCancelModalClose();
                form.resetFields();
            })
            .catch((info) => {
                console.log('Validate Failed:', info);
            });
    }, [updateStatusMutation, currentBooking, form]);

    const handleServicesModalClose = useCallback(() => {
        setServicesModalVisible(false);
        servicesForm.resetFields();
        setSelectedServices([]);
    }, [servicesForm]);

    const handleServiceChange = useCallback((serviceId, checked) => {
        if (checked) {
            setSelectedServices(prev => [...prev, serviceId]);
        } else {
            setSelectedServices(prev => prev.filter(id => id !== serviceId));
        }
    }, []);

    const handleCancelModalClose = useCallback(() => {
        setCancelModalVisible(false);
        form.resetFields();
    }, [form]);

    const handleTableChange = useCallback((pagination) => {
        const { setPagination } = getCurrentTabData;
        setPagination(pagination);
    }, [getCurrentTabData]);

    const handleSearch = useCallback((value) => {
        const currentFilters = getCurrentFilters;
        setCurrentFilters({ ...currentFilters, patientName: value });
        const { setPagination } = getCurrentTabData;
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [getCurrentFilters, setCurrentFilters, getCurrentTabData]);

    const handleStatusChange = useCallback((value) => {
        const currentFilters = getCurrentFilters;
        setCurrentFilters({ ...currentFilters, status: value });
        const { setPagination } = getCurrentTabData;
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [getCurrentFilters, setCurrentFilters, getCurrentTabData]);

    const handleClinicChange = useCallback((value) => {
        const currentFilters = getCurrentFilters;
        setCurrentFilters({ ...currentFilters, clinicId: value });
        const { setPagination } = getCurrentTabData;
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [getCurrentFilters, setCurrentFilters, getCurrentTabData]);

    const handleDateChange = useCallback((dates) => {
        const currentFilters = getCurrentFilters;
        setCurrentFilters({
            ...currentFilters,
            fromDate: dates ? dates[0] : undefined,
            toDate: dates ? dates[1] : undefined,
        });
        const { setPagination } = getCurrentTabData;
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [getCurrentFilters, setCurrentFilters, getCurrentTabData]);

    const handleResetFilters = useCallback(() => {
        switch (activeTab) {
            case 'today':
                setTodayFilters({
                    patientName: '',
                    clinicId: undefined,
                    fromDate: dayjs().startOf('day'),
                    toDate: dayjs().endOf('day'),
                });
                setTodayPagination(prev => ({ ...prev, current: 1 }));
                break;
            case 'tomorrow':
                setTomorrowFilters({
                    patientName: '',
                    clinicId: undefined,
                    fromDate: dayjs().add(1, 'day').startOf('day'),
                    toDate: dayjs().add(1, 'day').endOf('day'),
                });
                setTomorrowPagination(prev => ({ ...prev, current: 1 }));
                break;
            case 'yesterday':
                setYesterdayFilters({
                    patientName: '',
                    clinicId: undefined,
                    fromDate: undefined,
                    toDate: dayjs().subtract(1, 'day').endOf('day'),
                });
                setYesterdayPagination(prev => ({ ...prev, current: 1 }));
                break;
            case 'all':
                setAllFilters({
                    status: undefined,
                    patientName: '',
                    clinicId: undefined,
                    fromDate: undefined,
                    toDate: undefined,
                });
                setAllPagination(prev => ({ ...prev, current: 1 }));
                break;
        }
        if (isMobile) {
            setFiltersDrawerVisible(false);
        }
    }, [activeTab, isMobile]);// In BookingsScreen - Enhanced patient navigation
    const handlePatientAssignment = useCallback(async (selectedPatient) => {
        try {
            // Refresh the bookings list after successful assignment
            const { query } = getCurrentTabData;
            await query.refetch();

            // Close the modal
            setPatientAssignmentModalVisible(false);
            setCurrentGuestBooking(null);

            message.success(`Guest booking successfully assigned to ${selectedPatient.fullName}`);
        } catch (error) {
            console.error('Error handling patient assignment:', error);
        }
    }, [getCurrentTabData]);
    // In BookingsScreen component, add this handler
    const handleOpenReportModal = useCallback(async (booking) => {
        try {
            setLoadingStatus(true);
            
            await updateStatusMutation.mutateAsync({
                bookingId: booking.id,
                newStatus: 'Completed',
                serviceIds: []
            });

          
        } catch (error) {
            console.error('Error completing booking:', error);
            message.error('Failed to complete booking');
        }finally {
            setLoadingStatus(false);
        }
    }, [updateStatusMutation]);
    // Responsive filters component
    const FiltersContent = useMemo(() => {
        const currentFilters = getCurrentFilters;
        const isAllTab = activeTab === 'all';

        return (
            <div className="space-y-2">
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold text-gray-800 flex items-center m-0 ${isMobile ? 'text-sm' : 'text-md'}`}>
                        <FilterOutlined className="mr-2 text-sm" />
                        Filters & Search
                    </h3>
                    {isMobile && (
                        <Button
                            icon={<MenuOutlined />}
                            onClick={() => setFiltersDrawerVisible(false)}
                            type="text"
                            size="small"
                        />
                    )}
                </div>

                <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : isTablet ? 'md:grid-cols-2 gap-4' : `lg:grid-cols-${isAllTab ? '5' : '3'} gap-4`}`}>
                    <Input
                        placeholder="🔍 Search by patient name"
                        prefix={<SearchOutlined className="text-gray-400" />}
                        value={currentFilters.patientName}
                        onChange={(e) => handleSearch(e.target.value)}
                        allowClear
                        size={isMobile ? 'middle' : 'large'}
                        className="rounded-xl border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-colors duration-300"
                    />

                    <Select
                        placeholder="🏥 Filter by clinic"
                        allowClear
                        value={currentFilters.clinicId}
                        onChange={handleClinicChange}
                        loading={clinicsQuery.isLoading}
                        size={isMobile ? 'middle' : 'large'}
                        className="rounded-xl"
                    >
                        {clinicsQuery.data?.clinics?.map((clinic) => (
                            <Option key={clinic.id} value={clinic.id}>
                                {clinic.name}
                            </Option>
                        ))}
                    </Select>
                    <Select
                        placeholder="🏥 Filter by Status"
                        allowClear
                        value={currentStatus}
                        onChange={handleStatusChange}
                        size={isMobile ? 'middle' : 'large'}
                        className="rounded-xl"
                    >
                        {Object.keys(statusEnum).map((status) => (
                            <Option key={status} value={status}>
                                {status}
                            </Option>
                        ))}
                    </Select>

                    {isAllTab && (
                        <>
                            <Select
                                placeholder="📊 Filter by status"
                                allowClear
                                value={currentFilters.status}
                                onChange={handleStatusChange}
                                size={isMobile ? 'middle' : 'large'}
                                className="rounded-xl"
                            >
                                {Object.keys(statusConfig).map((status) => (
                                    <Option key={status} value={status}>
                                        {status}
                                    </Option>
                                ))}
                            </Select>

                            <RangePicker
                                format="MMM D, YYYY"
                                onChange={handleDateChange}
                                value={
                                    currentFilters.fromDate && currentFilters.toDate
                                        ? [dayjs(currentFilters.fromDate), dayjs(currentFilters.toDate)]
                                        : []
                                }
                                suffixIcon={<CalendarOutlined />}
                                size={isMobile ? 'middle' : 'large'}
                                className="w-full rounded-xl border-gray-200 hover:border-blue-400"
                            />
                        </>
                    )}

                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleResetFilters}
                        size={isMobile ? 'middle' : 'large'}
                        className="rounded-xl bg-primary border-none text-white hover:from-gray-600 hover:to-gray-700 transform hover:scale-105 transition-all duration-300"
                        block
                    >
                        {isMobile ? 'Reset' : 'Reset Filters'}
                    </Button>
                </div>
            </div>
        );
    }, [getCurrentFilters, activeTab, clinicsQuery.isLoading, clinicsQuery.data, handleSearch, handleClinicChange, handleStatusChange, handleDateChange, handleResetFilters, isMobile, isTablet]);

    // Memoized render functions
    const renderFilters = useMemo(() => {
        if (isMobile) {
            return (
                <>
                    {/* Mobile Filter Button */}
                    <div className="mb-4 flex justify-between items-center">
                        <Button
                            icon={<FilterOutlined />}
                            onClick={() => setFiltersDrawerVisible(true)}
                            className="rounded-xl bg-primary border-none text-white"
                        >
                            Filters
                        </Button>
                    </div>

                    {/* Mobile Filter Drawer */}
                    <Drawer
                        title="Filters & Search"
                        placement="bottom"
                        closable={true}
                        onClose={() => setFiltersDrawerVisible(false)}
                        open={filtersDrawerVisible}
                        height="auto"
                        className="mobile-filters-drawer"
                    >
                        {FiltersContent}
                    </Drawer>
                </>
            );
        }

        return (
            <Card className="mb-6 sm:mb-8 shadow-sm border-0 bg-white/80 backdrop-blur-sm p-3 sm:p-0">
                {FiltersContent}
            </Card>
        );
    }, [isMobile, FiltersContent, filtersDrawerVisible]);

    const renderBookingsContent = useMemo(() => {
        const { query, pagination } = getCurrentTabData;
        const gridColsClass = `grid-cols-${getGridCols}`;

        return (
            <Spin spinning={query.isLoading || clinicsQuery.isLoading}>
                {query.data?.bookings?.length > 0 ? (
                    <>
                        <div className={`grid grid-cols-1 ${isMobile ? 'gap-4' :
                            isTablet ? 'sm:grid-cols-2 gap-4 sm:gap-6' :
                                'sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6'
                            } mb-6 sm:mb-8`}>
                            {query.data.bookings.map((booking, index) => (
                                <BookingCard
                                loadingStatus={loadingStatus}
                                    key={booking.id}
                                    booking={booking}
                                    index={index}
                                    onGuestAssignment={handleGuestAssignmentClick}
                                    onComplete={handleCompleteClick}
                                    onCancel={handleCancelClick}
                                    onOpenReportModal={handleOpenReportModal} // Add this
                                    updateStatusMutation={updateStatusMutation}
                                    isMobile={isMobile}
                                           onEditInvoice={handleEditInvoice}      // NEW
                  onCreateInvoice={handleCreateInvoice}  // NEW
                  completing={completing}
                                    onMakeActive={handleMakeActive}

                                />
                            ))}
                        </div>

                        {/* Enhanced Responsive Pagination */}
                        <div className="flex justify-center">
                            <div className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${isMobile ? 'p-2' : 'p-4'}`}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={query.data?.totalCount || 0}
                                    onChange={(page, pageSize) => {
                                        const { setPagination } = getCurrentTabData;
                                        setPagination({
                                            ...pagination,
                                            current: page,
                                            pageSize,
                                        });
                                    }}
                                    showTotal={(total) => (
                                        <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : ''}`}>
                                            {isMobile ? `${total} total` : `Total ${total} bookings`}
                                        </span>
                                    )}
                                    size={isMobile ? 'small' : 'default'}
                                    showSizeChanger={!isMobile}
                                    showQuickJumper={!isMobile}
                                    className="modern-pagination"
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    !query.isLoading && (
                        <div className="text-center py-12 sm:py-16">
                            <div className={`${isMobile ? 'text-4xl' : 'text-6xl'} mb-4`}>📅</div>
                            <h3 className={`font-bold text-gray-600 mb-2 ${isMobile ? 'text-lg' : 'text-2xl'}`}>
                                No Bookings Found
                            </h3>
                            <p className={`text-gray-500 ${isMobile ? 'text-sm' : ''}`}>
                                Try adjusting your filters or check back later.
                            </p>
                        </div>
                    )
                )}
            </Spin>
        );
    }, [getCurrentTabData, getGridCols, clinicsQuery.isLoading, handleCompleteClick, handleCancelClick, updateStatusMutation, isMobile, isTablet]);

    // Memoized tab title function
    const getTabTitle = useCallback((key, count) => {
        const titles = {
            today: isMobile ? `Today (${count || 0})` : `📅 Today (${count || 0})`,
            tomorrow: isMobile ? `Tomorrow (${count || 0})` : `📅 Coming Tomorrow (${count || 0})`,
            yesterday: isMobile ? `Previous (${count || 0})` : `📅 Previous (${count || 0})`,
            all: isMobile ? `All (${count || 0})` : `📋 All Bookings (${count || 0})`
        };
        return titles[key];
    }, [isMobile]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
            {/* Add custom responsive CSS for animations */}
            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes shimmer {
                    0% {
                        background-position: -1000px 0;
                    }
                    100% {
                        background-position: 1000px 0;
                    }
                }
                
                .shimmer {
                    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                    background-size: 1000px 100%;
                    animation: shimmer 2s infinite;
                }

                .ant-tabs-tab {
                    border-width: 1px;
                    border-style: solid;
                    border-color: #298E9F !important;
                    margin-right: ${isMobile ? '4px' : '8px'} !important;
                    padding: ${isMobile ? '4px 8px' : '8px 16px'} !important;
                    font-size: ${isMobile ? '12px' : '14px'} !important;
                }
                .ant-tabs-tab-active {
                    border-bottom: ${isMobile ? '6px' : '9px'} !important;
                    color: white !important;
                }

                .mobile-filters-drawer .ant-drawer-body {
                    padding: 16px !important;
                }

                .modern-pagination .ant-pagination-item {
                    margin: ${isMobile ? '0 2px' : '0 4px'} !important;
                }

                @media (max-width: 640px) {
                    .ant-tabs-content-holder {
                        padding: 0 !important;
                    }
                    
                    .ant-card-body {
                        padding: 12px !important;
                    }
                }

                @media (max-width: 768px) {
                    .ant-modal {
                        margin: 0 !important;
                        max-width: none !important;
                    }
                }
            `}</style>

            <div className={`${isMobile ? 'p-4' : 'p-6'}`}>


                {/* Responsive Tabs */}
                <Card className={`mb-4 sm:mb-8 shadow-sm border-0 bg-white/80 backdrop-blur-sm ${isMobile ? 'px-2' : ''}`}>
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        type="line"
                        size={isMobile ? 'small' : 'large'}
                        className="custom-tabs"
                        tabBarGutter={isMobile ? 4 : 8}
                    >
                        <TabPane
                            tab={getTabTitle('today', todayBookingsQuery.data?.totalCount)}
                            key="today"
                        />
                        <TabPane
                            tab={getTabTitle('tomorrow', tomorrowBookingsQuery.data?.totalCount)}
                            key="tomorrow"
                        />
                        <TabPane
                            tab={getTabTitle('yesterday', yesterdayBookingsQuery.data?.totalCount)}
                            key="yesterday"
                        />
                        <TabPane
                            tab={getTabTitle('all', allBookingsQuery.data?.totalCount)}
                            key="all"
                        />
                    </Tabs>
                </Card>

                {renderFilters}

                {/* Responsive Bookings Content */}
                {renderBookingsContent}
            </div>

            {/* Enhanced Responsive Services Selection Modal */}
            <ServiceModal
                visible={servicesModalVisible}
                onCancel={handleServicesModalClose}
                onOk={handleCompleteConfirm}
                loading={updateStatusMutation.isLoading}
                services={doctorServices}
                servicesLoading={servicesLoading}
                selectedServices={selectedServices}
                onServiceChange={handleServiceChange}
                form={servicesForm}
                isMobile={isMobile}
            />
            <PatientAssignmentModal
                visible={patientAssignmentModalVisible}
                onCancel={handlePatientAssignmentModalClose}
                onAssign={handlePatientAssignment}
                booking={currentGuestBooking}
                isMobile={isMobile}
            />
            {/* Enhanced Responsive Cancel Booking Modal */}
            <Modal
                title={
                    <div className="flex items-center">
                        <div className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'} bg-gradient-to-r from-red-400 to-red-600 rounded-full flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                            <TbCancel className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
                        </div>
                        <span className={isMobile ? 'text-sm' : ''}>Cancel Booking</span>
                    </div>
                }
                open={cancelModalVisible}
                onOk={handleCancelConfirm}
                onCancel={handleCancelModalClose}
                okText=" Confirm Cancellation"
                cancelText="Close"
                confirmLoading={updateStatusMutation.isLoading}
                width={isMobile ? '95vw' : 500}
                className="modern-modal"
                centered={isMobile}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="reason"
                        label={<span className={isMobile ? 'text-sm' : ''}>Cancellation Reason</span>}
                        rules={[{ required: true, message: 'Please provide a cancellation reason' }]}
                    >
                        <TextArea
                            rows={isMobile ? 3 : 4}
                            placeholder="Enter the reason for cancellation..."
                            className="rounded-xl"
                            size={isMobile ? 'middle' : 'large'}
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <AddReportModal
                open={addReportModalVisible}
                onClose={() => {
                    setAddReportModalVisible(false);
                    setSelectedPatientForReport(null);
                }}
                patient={selectedPatientForReport}
                setPatient={setSelectedPatientForReport}
                onRefreshSchedules={() => {
                    queryClient.invalidateQueries(['schedules']);
                }}
                change={false} 
                setChange={() => { }} 
                booking={selectedBooking}
                setServicesModalVisible={setServicesModalVisible}
            />
            <ReportModal
                isPrivateReport={isPrivate}
                visible={reportModalVisible}
                onCancel={() => setReportModalVisible(false)}
                bookingId={currentReportBooking?.id}
                reportId={currentReportBooking?.reportId}
                setCurrentReportId={(id) => {
                    setCurrentReportBooking((prev) => ({ ...prev, reportId: id }));
                }}
                setCurrentReportBooking={setCurrentReportBooking}
                onSuccess={() => {
                    queryClient.invalidateQueries({
                        queryKey: ['bookings'],
                        refetchType: 'active'
                    });
                }}
                isMobile={isMobile}
            />
             <InvoiceModal
      open={invoiceModalVisible}
      onClose={() => {
        setInvoiceModalVisible(false);
        setInvoiceData(null);
        setCurrentInvoiceBooking(null);
      }}
      invoiceData={invoiceData}
      onRefresh={async () => {
        // Refresh invoice data after edit/payment
        if (currentInvoiceBooking) {
          const response = await drEasyApi.get('/api/BookingInvoice/Get', {
            params: { BookingId: currentInvoiceBooking.id }
          });
          setInvoiceData(response.data);
          
          // Refresh bookings list
          const { query } = getCurrentTabData;
          await query.refetch();
        }
      }}
    />
        </div>
    );
};

// Set display names for better debugging
BookingCard.displayName = 'BookingCard';
ServiceModal.displayName = 'ServiceModal';

export default BookingsScreen;